/**
 * Page Contrats Employés (Admin)
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { FileSignature, Plus, History, Search, Wallet, DollarSign } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const fmt = (n, c = 'XOF') => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${c}`;

const empty = {
  contractType: 'CDI', startDate: '', endDate: '', baseSalary: '', currency: 'XOF',
  workTimePercent: 100, category: '', paymentMode: 'VIREMENT', bankAccount: '', mobileNumber: '',
};

function ContractDialog({ open, onOpenChange, employee, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm({ ...empty, startDate: new Date().toISOString().slice(0, 10) }); }, [open]);

  const submit = async () => {
    if (!form.startDate || !form.baseSalary) {
      toast.error('Date de début et salaire requis');
      return;
    }
    try {
      setBusy(true);
      await api.post('/payroll/contracts', {
        ...form,
        employeeId: employee.id,
        baseSalary: parseFloat(form.baseSalary),
        workTimePercent: parseFloat(form.workTimePercent),
        endDate: form.endDate || null,
      });
      toast.success('Contrat créé (l\'ancien a été clôturé)');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau contrat pour {employee?.firstName} {employee?.lastName}</DialogTitle>
          <DialogDescription>L'éventuel contrat actif sera clôturé à cette date.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label>Type</Label>
            <Select value={form.contractType} onValueChange={v => setForm({ ...form, contractType: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CDI">CDI</SelectItem>
                <SelectItem value="CDD">CDD</SelectItem>
                <SelectItem value="STAGE">Stage</SelectItem>
                <SelectItem value="CONSULTANT">Consultant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Catégorie</Label>
            <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              placeholder="Cadre / Agent / Ouvrier" />
          </div>
          <div>
            <Label>Date début *</Label>
            <Input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
          </div>
          <div>
            <Label>Date fin (CDD)</Label>
            <Input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
          </div>
          <div>
            <Label>Salaire de base *</Label>
            <Input type="number" value={form.baseSalary}
              onChange={e => setForm({ ...form, baseSalary: e.target.value })} data-testid="contract-base-salary" />
          </div>
          <div>
            <Label>Devise</Label>
            <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="XOF">XOF (CFA)</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Temps de travail (%)</Label>
            <Input type="number" min="10" max="100" value={form.workTimePercent}
              onChange={e => setForm({ ...form, workTimePercent: e.target.value })} />
          </div>
          <div>
            <Label>Mode de paiement</Label>
            <Select value={form.paymentMode} onValueChange={v => setForm({ ...form, paymentMode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="VIREMENT">Virement bancaire</SelectItem>
                <SelectItem value="MOBILE_MONEY">Mobile Money</SelectItem>
                <SelectItem value="CHEQUE">Chèque</SelectItem>
                <SelectItem value="ESPECES">Espèces</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.paymentMode === 'VIREMENT' && (
            <div className="col-span-2">
              <Label>IBAN / Compte bancaire</Label>
              <Input value={form.bankAccount}
                onChange={e => setForm({ ...form, bankAccount: e.target.value })} />
            </div>
          )}
          {form.paymentMode === 'MOBILE_MONEY' && (
            <div className="col-span-2">
              <Label>Numéro Mobile Money</Label>
              <Input value={form.mobileNumber}
                onChange={e => setForm({ ...form, mobileNumber: e.target.value })} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="save-contract-btn">
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PayrollContractsPage() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [activeContract, setActiveContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/employees', { params: { limit: 1000 } });
      const items = data.employees || data.items || (Array.isArray(data) ? data : []);
      setEmployees(items);
    } catch (e) {
      toast.error('Chargement employés impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContracts = useCallback(async (empId) => {
    try {
      const [{ data: list }, activeRes] = await Promise.all([
        api.get(`/payroll/contracts/${empId}`),
        api.get(`/payroll/contracts/${empId}/active`).catch(() => ({ data: null })),
      ]);
      setContracts(list.items || []);
      setActiveContract(activeRes.data);
    } catch (e) {
      toast.error('Chargement contrats impossible');
    }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { if (selected) loadContracts(selected.id); }, [selected, loadContracts]);

  const filtered = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (e.firstName || '').toLowerCase().includes(s) ||
      (e.lastName || '').toLowerCase().includes(s) ||
      (e.matricule || '').toLowerCase().includes(s)
    );
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="payroll-contracts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileSignature className="w-7 h-7 text-indigo-600" /> Contrats & Rémunération
            </h1>
            <p className="text-slate-500 mt-1">Un contrat actif par employé, historique conservé.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employés */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Employés ({employees.length})</CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                <Input className="pl-8" placeholder="Rechercher…" value={search}
                  onChange={e => setSearch(e.target.value)} data-testid="emp-search" />
              </div>
            </CardHeader>
            <CardContent className="space-y-1 max-h-[60vh] overflow-y-auto">
              {loading ? (
                <p className="text-sm text-slate-500">Chargement…</p>
              ) : (
                filtered.map(e => (
                  <button key={e.id}
                    onClick={() => setSelected(e)}
                    data-testid={`emp-${e.id}`}
                    className={`w-full text-left p-2 rounded transition ${
                      selected?.id === e.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50'
                    }`}>
                    <div className="font-medium text-sm">{e.firstName} {e.lastName}</div>
                    <div className="text-xs text-slate-500">{e.matricule || '—'} • {e.jobTitle || ''}</div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Détail */}
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              {!selected ? (
                <div className="text-center py-12">
                  <FileSignature className="w-12 h-12 mx-auto text-slate-300" />
                  <p className="mt-3 text-slate-600">Sélectionnez un employé pour voir ses contrats.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold">{selected.firstName} {selected.lastName}</h2>
                      <p className="text-slate-500">{selected.jobTitle || '—'} • Matricule {selected.matricule || '—'}</p>
                    </div>
                    <Button onClick={() => setDialogOpen(true)} data-testid="new-contract-btn">
                      <Plus className="w-4 h-4 mr-2" /> Nouveau contrat
                    </Button>
                  </div>

                  {/* Actif */}
                  {activeContract ? (
                    <Card className="border-emerald-200 bg-emerald-50/40 mb-6">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wallet className="w-4 h-4 text-emerald-600" />
                          Contrat actif
                          {activeContract.fallback && <Badge variant="outline" className="text-xs">depuis fiche employé</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-slate-500">Type</div>
                          <div className="font-medium">{activeContract.contractType || 'CDI'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Début</div>
                          <div className="font-medium">{activeContract.startDate?.slice(0, 10) || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Salaire de base</div>
                          <div className="font-bold text-lg text-emerald-700">
                            {fmt(activeContract.baseSalary, activeContract.currency || 'XOF')}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Temps</div>
                          <div className="font-medium">{activeContract.workTimePercent || 100}%</div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="mb-6 p-4 border-2 border-dashed rounded-lg text-center text-slate-500">
                      Aucun contrat actif — créez-en un pour activer la paie.
                    </div>
                  )}

                  {/* Historique */}
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-slate-500" /> Historique ({contracts.length})
                  </h3>
                  {contracts.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">Aucun historique</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                          <tr>
                            <th className="text-left py-2 px-3">Période</th>
                            <th className="text-left py-2 px-3">Type</th>
                            <th className="text-right py-2 px-3">Salaire</th>
                            <th className="text-left py-2 px-3">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contracts.map(c => (
                            <tr key={c.id} className="border-t">
                              <td className="py-2 px-3">
                                {c.startDate?.slice(0, 10)} → {c.endDate?.slice(0, 10) || 'en cours'}
                              </td>
                              <td className="py-2 px-3">{c.contractType}</td>
                              <td className="py-2 px-3 text-right font-medium">{fmt(c.baseSalary, c.currency)}</td>
                              <td className="py-2 px-3">
                                {c.isActive
                                  ? <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
                                  : <Badge variant="outline">Clôturé</Badge>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <ContractDialog open={dialogOpen} onOpenChange={setDialogOpen} employee={selected}
          onSaved={() => loadContracts(selected.id)} />
      </div>
    </DashboardLayout>
  );
}
